/**
 * Seed source for each component's workspace, keyed by nav slug.
 *
 * These are reduced from legion-ui's `src/gallery/sections/<Name>.tsx` — the
 * gallery renders an exhaustive matrix, which is right for screenshot
 * baselines and wrong for a first example. One focused snippet per page.
 *
 * A slug with no entry falls back to a "coming soon" note, so the site stays
 * coherent while the page batches land.
 */
export const SEEDS: Record<string, string> = {
  button: `<Stack direction="row" gap={3} align="center">
  <Button variant="primary">Save changes</Button>
  <Button variant="secondary">Preview</Button>
  <Button variant="tertiary">Learn more</Button>
  <Button variant="danger">Delete</Button>
</Stack>`,

  alert: `<Stack gap={3}>
  <Alert severity="info">A new version is available.</Alert>
  <Alert severity="success">Your changes were saved.</Alert>
  <Alert severity="warning">This action cannot be undone.</Alert>
  <Alert severity="error">Something went wrong.</Alert>
</Stack>`,

  chip: `<Stack direction="row" gap={2} align="center">
  <Chip>Ambient</Chip>
  <Chip variant="outline">Jazz</Chip>
  <Chip variant="accent">Favourites</Chip>
</Stack>`,

  text: `<Stack gap={2}>
  <Text variant="display">Display</Text>
  <Text variant="title">Title</Text>
  <Text variant="body">Body copy sits at the default size.</Text>
  <Text variant="mono" tone="muted">mono / muted</Text>
</Stack>`,

  stack: `<Stack gap={3}>
  <Stack direction="row" gap={2}>
    <Button size="sm">One</Button>
    <Button size="sm">Two</Button>
    <Button size="sm">Three</Button>
  </Stack>
  <Divider />
  <Stack gap={2} align="start">
    <Text>Column, gap 2</Text>
    <Text tone="muted">Second line</Text>
  </Stack>
</Stack>`,
  surface: `<Stack direction="row" gap={3}>
  <Surface level="flat" style={{ padding: 16 }}>flat</Surface>
  <Surface level="panel" style={{ padding: 16 }}>panel</Surface>
  <Surface level="raised" style={{ padding: 16 }}>raised</Surface>
</Stack>`,
  card: `<Card style={{ maxWidth: 320 }}>
  <Card.Content>
    <Text variant="subheading">Nocturne in E flat</Text>
    <Text tone="muted">Chopin · 4:32</Text>
  </Card.Content>
  <Card.Actions>
    <Button size="sm">Play</Button>
    <Button size="sm" variant="tertiary">Queue</Button>
  </Card.Actions>
</Card>`,
  divider: `<Stack gap={3} style={{ width: 280 }}>
  <Text>Above</Text>
  <Divider />
  <Text>Below</Text>
  <Divider spacing={3} inset />
</Stack>`,
  'tile-grid': `<TileGrid min={120} gap={3}>
  {['Jazz', 'Ambient', 'Classical', 'Folk', 'Electronic'].map((name) => (
    <Surface key={name} level="panel" style={{ padding: 16, textAlign: 'center' }}>
      {name}
    </Surface>
  ))}
</TileGrid>`,
  collapse: `const [open, setOpen] = useState(false)
return (
  <Stack gap={3} align="start">
    <Button size="sm" onClick={() => setOpen(!open)}>
      {open ? 'Hide' : 'Show'} details
    </Button>
    <Collapse in={open}>
      <Surface level="panel" style={{ padding: 16 }}>
        <Text>Height animates from zero, so nothing jumps.</Text>
      </Surface>
    </Collapse>
  </Stack>
)`,
  'icon-button': `<Stack direction="row" gap={2} align="center">
  <IconButton size="sm" label="Play">
    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
  </IconButton>
  <IconButton size="md" label="Play">
    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
  </IconButton>
  <IconButton size="lg" variant="glass" label="Play">
    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
  </IconButton>
</Stack>`,
  'button-group': `const [value, setValue] = useState('all')
return (
  <ButtonGroup label="Filter" value={value} onChange={setValue}>
    <ButtonGroup.Item value="all">All</ButtonGroup.Item>
    <ButtonGroup.Item value="albums">Albums</ButtonGroup.Item>
    <ButtonGroup.Item value="artists">Artists</ButtonGroup.Item>
  </ButtonGroup>
)`,
  input: `<Stack gap={3} style={{ width: 280 }}>
  <Input placeholder="Search the library" />
  <Input defaultValue="Miles Davis" />
  <Input placeholder="Disabled" disabled />
  <Input placeholder="Invalid" invalid />
</Stack>`,
  select: `<Stack gap={3} style={{ width: 280 }}>
  <Select defaultValue="added">
    <option value="added">Recently added</option>
    <option value="played">Recently played</option>
    <option value="title">Title</option>
  </Select>
  <Select disabled defaultValue="added">
    <option value="added">Disabled</option>
  </Select>
</Stack>`,
  checkbox: `const [checked, setChecked] = useState(true)
return (
  <Stack gap={2} align="start">
    <Checkbox
      checked={checked}
      onChange={(e) => setChecked(e.target.checked)}
      label="Shuffle"
    />
    <Checkbox indeterminate label="Some selected" />
    <Checkbox disabled label="Disabled" />
  </Stack>
)`,
  switch: `const [on, setOn] = useState(true)
return (
  <Stack gap={2} align="start">
    <Switch checked={on} onChange={(e) => setOn(e.target.checked)} label="Gapless playback" />
    <Switch disabled label="Disabled" />
  </Stack>
)`,
  rating: `const [value, setValue] = useState(3)
return (
  <Stack gap={3} align="start">
    <Rating
      name="demo-rating"
      value={value}
      onChange={(event, next) => setValue(next ?? 0)}
    />
    <Text tone="muted">{value} of 5</Text>
    <Rating name="demo-readonly" value={4} readOnly />
  </Stack>
)`,
  'form-field': `<Stack gap={4} style={{ width: 320 }}>
  <FormField label="Library name" helperText="Shown in the sidebar.">
    <Input defaultValue="Main" />
  </FormField>
  <FormField label="Port" error="Must be between 1 and 65535.">
    <Input defaultValue="99999" invalid />
  </FormField>
</Stack>`,
  fieldset: `<Fieldset legend="Playback" style={{ width: 320 }}>
  <Stack gap={2} align="start">
    <Checkbox label="Gapless" defaultChecked />
    <Checkbox label="Normalise volume" />
    <Checkbox label="Crossfade" />
  </Stack>
</Fieldset>`,
  spinner: `<Stack direction="row" gap={4} align="center">
  <Spinner size="sm" />
  <Spinner size="md" />
  <Spinner size="lg" />
</Stack>`,
  progress: `const [value, setValue] = useState(40)
return (
  <Stack gap={3} style={{ width: 320 }}>
    <Progress value={value} label="Import progress" />
    <Progress label="Scanning library" />
    <Button size="sm" onClick={() => setValue((v) => (v + 20) % 120)}>
      Advance
    </Button>
  </Stack>
)`,
  badge: `<Stack direction="row" gap={4} align="center">
  <Badge badgeContent={4}>
    <Button variant="secondary">Queue</Button>
  </Badge>
  <Badge variant="dot" tone="danger">
    <Button variant="secondary">Alerts</Button>
  </Badge>
</Stack>`,
  tabs: `const [tab, setTab] = useState('albums')
return (
  <Stack gap={3} style={{ width: 380 }}>
    <Tabs value={tab} onChange={setTab}>
      <Tabs.Tab value="albums">Albums</Tabs.Tab>
      <Tabs.Tab value="artists">Artists</Tabs.Tab>
      <Tabs.Tab value="songs">Songs</Tabs.Tab>
    </Tabs>
    <Text tone="muted">Showing: {tab}</Text>
  </Stack>
)`,
  link: `<Stack gap={2} align="start">
  <Link href="#">A link in running text</Link>
  <Link href="#" tone="muted">Muted</Link>
</Stack>`,
  accordion: `<Stack gap={2} style={{ width: 380 }}>
  <Accordion defaultExpanded>
    <Accordion.Summary>Transcoding</Accordion.Summary>
    <Accordion.Details>
      <Text>Only transcode when the client cannot play the source.</Text>
    </Accordion.Details>
  </Accordion>
  <Accordion>
    <Accordion.Summary>Scanning</Accordion.Summary>
    <Accordion.Details>
      <Text>Rescan on a schedule, or when the filesystem changes.</Text>
    </Accordion.Details>
  </Accordion>
</Stack>`,
  menu: `const [open, setOpen] = useState(false)
const anchor = useRef(null)
return (
  <>
    <Button ref={anchor} onClick={() => setOpen(!open)}>Actions</Button>
    <Menu open={open} anchorEl={anchor.current} onClose={() => setOpen(false)}>
      <Menu.Item onClick={() => setOpen(false)}>Play next</Menu.Item>
      <Menu.Item onClick={() => setOpen(false)}>Add to queue</Menu.Item>
      <Menu.Item onClick={() => setOpen(false)}>Download</Menu.Item>
    </Menu>
  </>
)`,
  icon: `<Stack direction="row" gap={3} align="center">
  {['sm', 'md', 'lg', 'xl'].map((size) => (
    <Icon key={size} size={size}>
      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
    </Icon>
  ))}
</Stack>`,
  avatar: `<Stack direction="row" gap={3} align="center">
  <Avatar size="sm">MD</Avatar>
  <Avatar size="md">Miles Davis</Avatar>
  <Avatar size="lg" shape="square">JC</Avatar>
</Stack>`,
  tag: `<Stack direction="row" gap={2} align="center">
  <Tag>FLAC</Tag>
  <Tag tone="accent">Hi-Res</Tag>
  <Tag tone="neutral">MP3</Tag>
</Stack>`,
  list: `<List style={{ width: 340 }}>
  <List.Item>
    <List.Text primary="So What" secondary="Miles Davis" />
  </List.Item>
  <List.Item>
    <List.Text primary="Blue in Green" secondary="Miles Davis" />
  </List.Item>
  <List.Item>
    <List.Text primary="All Blues" secondary="Miles Davis" />
  </List.Item>
</List>`,
  table: `<Table style={{ width: 420 }}>
  <Table.Head>
    <Table.Row>
      <Table.Cell>Title</Table.Cell>
      <Table.Cell>Artist</Table.Cell>
      <Table.Cell numeric>Length</Table.Cell>
    </Table.Row>
  </Table.Head>
  <Table.Body>
    <Table.Row>
      <Table.Cell>So What</Table.Cell>
      <Table.Cell>Miles Davis</Table.Cell>
      <Table.Cell numeric>9:22</Table.Cell>
    </Table.Row>
    <Table.Row>
      <Table.Cell>Blue in Green</Table.Cell>
      <Table.Cell>Miles Davis</Table.Cell>
      <Table.Cell numeric>5:37</Table.Cell>
    </Table.Row>
  </Table.Body>
</Table>`,
  dialog: `const [open, setOpen] = useState(false)
return (
  <>
    <Button onClick={() => setOpen(true)}>Delete playlist</Button>
    <Dialog open={open} onClose={() => setOpen(false)} size="sm">
      <Dialog.Title onClose={() => setOpen(false)}>Delete playlist?</Dialog.Title>
      <Dialog.Content>
        <Text>This cannot be undone.</Text>
      </Dialog.Content>
      <Dialog.Actions>
        <Button variant="tertiary" onClick={() => setOpen(false)}>Cancel</Button>
        <Button variant="danger" onClick={() => setOpen(false)}>Delete</Button>
      </Dialog.Actions>
    </Dialog>
  </>
)`,
  tooltip: `<Stack direction="row" gap={4} align="center">
  <Tooltip title="Play all tracks">
    <Button variant="secondary">Hover me</Button>
  </Tooltip>
  <Tooltip title="Also opens on focus" placement="bottom">
    <Button variant="secondary">Or focus me</Button>
  </Tooltip>
</Stack>`,
  popover: `const [open, setOpen] = useState(false)
const anchor = useRef(null)
return (
  <>
    <Button ref={anchor} onClick={() => setOpen(!open)}>Details</Button>
    <Popover open={open} anchorEl={anchor.current} onClose={() => setOpen(false)}>
      <div style={{ padding: 16, maxWidth: 240 }}>
        <Text variant="subheading">Kind of Blue</Text>
        <Text tone="muted">Columbia, 1959</Text>
      </div>
    </Popover>
  </>
)`,
  'legion-provider': `<Stack gap={3}>
  <LegionProvider theme={{ accent: '#3e764b' }}>
    <Stack direction="row" gap={2} align="center">
      <Button>Green accent</Button>
      <Chip variant="accent">Chip</Chip>
    </Stack>
  </LegionProvider>
  <LegionProvider theme={{ radiusScale: '0' }}>
    <Button>Square corners</Button>
  </LegionProvider>
</Stack>`,
}

export function seedFor(slug: string): string | undefined {
  return SEEDS[slug]
}
